import { consentIdSystem } from '../components/ManageConsent/View/constants';

export { consentIdSystem as consentFormIdSystem } from '../components/ManageConsent/View/constants';

type QuestionnaireWithVersion = fhir.Questionnaire & { version?: string };

/** Best-effort semver compare for published consent form versions (matches MOPS `semverCompare` for valid semver). */
function compareSemverLike(a: string, b: string): number {
  const pa = a.split('.').map(p => parseInt(p, 10) || 0);
  const pb = b.split('.').map(p => parseInt(p, 10) || 0);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) {
      return da < db ? -1 : 1;
    }
  }
  return 0;
}

/**
 * Latest **active** published `Questionnaire` for a given consent form id, matching
 * `assignLatestSecondaryConsent` in member-ops `src/utils/consent.ts`.
 */
export function findLatestActiveQuestionnaireForForm(
  questionnaires: QuestionnaireWithVersion[],
  consentFormId: string,
): QuestionnaireWithVersion | undefined {
  return questionnaires
    .filter(q => q.status === 'active')
    .filter(q => {
      const formId = q.identifier?.find(
        i => i.system === consentIdSystem,
      )?.value;
      return formId === consentFormId;
    })
    .reduce<QuestionnaireWithVersion | undefined>((latest, next) => {
      if (!latest) {
        return next;
      }
      if (!next.version || !latest.version) {
        return next.id && latest.id ? next : latest;
      }
      return compareSemverLike(next.version, latest.version) > 0
        ? next
        : latest;
    }, undefined);
}

function consentHasQuestionnaire(
  resource: fhir.Consent,
  questionnaireId: string,
): boolean {
  return Boolean(resource.meta?.tag?.some(t => t.code === questionnaireId));
}

/**
 * If an existing matching consent is found, assignment is skipped (see `getExistingConsent`
 * in member-ops). When `reassignActiveConsents` is true, only **proposed** directives
 * are considered, so an existing **active** assignment does not block a new proposed one.
 */
export function findConflictingExistingConsent(
  items: fhir.Consent[],
  questionnaireId: string,
  reassignActiveConsents: boolean,
): fhir.Consent | undefined {
  return items.find(c => {
    if (reassignActiveConsents) {
      if (c.status !== 'proposed') {
        return false;
      }
    } else if (c.status !== 'proposed' && c.status !== 'active') {
      return false;
    }
    return consentHasQuestionnaire(c, questionnaireId);
  });
}
